"use client";

import Image from "next/image";

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { priceFormatter } from "@/lib/price-formatter";
import { absoluteUrl, cn } from "@/lib/utils";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { selectFilterOptions } from "@/feature/flight/search/state/selectors";
import {
  setFilters,
} from "@/feature/flight/search/state/slices/extract-flight-filter.slice";

const CheapAirlineFilter = () => {
  const dispatch = useAppDispatch();
  const { selectOption, defaultOption } = useAppSelector(
    selectFilterOptions,
  );
  const isResponseEnd = useAppSelector((state) => state.flightSearchStatus.isResponseEnd);
  const isLoading = useAppSelector((state) => state.flightSearchStatus.isLoading);
  const isBusy = isLoading || !isResponseEnd;

  const selected = selectOption?.airline;
  const airlineList = defaultOption?.airlines_lowest_price;
  const showSkeleton = isBusy && (!airlineList || airlineList.length === 0);

  const airlineClick = (value: string) => {
    if (isBusy) return; // prevent interactions while loading
    const isSelected = selected?.some((airline) => airline === value);

    const updatedAirlines = isSelected
      ? selected?.filter((airline) => airline !== value)
      : [...(selected ?? []), value];

    dispatch(setFilters({ airline: updatedAirlines }));
  };

  return (
    <div className="relative">
      {/* edge fade gradients, responsive to match container padding */}
      <div className="pointer-events-none absolute inset-y-0 left-3 sm:left-6 md:left-8 lg:left-12 w-6 sm:w-8 md:w-10 lg:w-12 bg-gradient-to-r from-muted z-10"></div>
      <div className="pointer-events-none absolute inset-y-0 right-3 sm:right-6 md:right-8 lg:right-12 w-6 sm:w-8 md:w-10 lg:w-12 bg-gradient-to-l from-muted z-10"></div>
      <Carousel
        opts={{
          align: "start",
          loop: true,
        }}
        // plugins={[
        //   Autoplay({
        //     delay: 3000,
        //   }),
        // ]}
        className="w-full max-w-screen-xl mx-auto px-3 sm:px-6 md:px-8 lg:px-12 rounded-md relative overflow-hidden"
      >
        <CarouselContent className="">
          {/* shadow */}

          {showSkeleton
            ? Array.from({ length: 8 }).map((_, index) => (
                <CarouselItem
                  key={`skeleton-${index}`}
                  className="basis-1/2 md:basis-1/2 lg:basis-1/3 xl:basis-1/4 pl-3 sm:pl-4 md:pl-5"
                >
                  <div className="p-1 shadow rounded-md my-1 bg-background overflow-hidden">
                    <div className="flex aspect-[8/1] items-center justify-center">
                      <div className="grid grid-cols-12 w-full justify-items-center min-w-0">
                        <div className="col-span-4 w-full flex items-center justify-end pr-2">
                          <div className="size-10 rounded-full bg-muted animate-pulse" />
                        </div>
                        <div className="col-span-8 w-full space-y-1 min-w-0">
                          <div className="h-3 w-24 bg-muted rounded animate-pulse" />
                          <div className="h-3 w-16 bg-muted rounded animate-pulse" />
                        </div>
                      </div>
                    </div>
                  </div>
                </CarouselItem>
              ))
            : airlineList?.map((airline, index) => (
                <CarouselItem
                  key={index}
                  className="basis-1/2 md:basis-1/2 lg:basis-1/3 xl:basis-1/4 pl-3 sm:pl-4 md:pl-5"
                >
                  <div
                    onClick={() => airlineClick(airline.airline_name)}
                    className={cn(
                      "cursor-pointer p-1 shadow rounded-md my-1 bg-background overflow-hidden",
                      isBusy &&
                        "opacity-60 cursor-not-allowed hover:bg-background",
                      (selected?.includes(airline.airline_name) ?? false)
                        ? "bg-primary/10 text-primary"
                        : "hover:bg-accent",
                    )}
                    aria-disabled={isBusy}
                  >
                    <div className="flex aspect-[8/1] items-center justify-center ">
                      <div className="grid grid-cols-12 w-full justify-items-center min-w-0">
                        <div className="col-span-4 w-full flex items-center justify-end pr-2">
                          <div className="size-10">
                            {airline.airline_logo ? (
                              <Image
                                className="w-full h-full rounded-full object-cover border"
                                src={absoluteUrl(airline.airline_logo, true)}
                                alt={airline.airline_name}
                                width={32}
                                height={32}
                                sizes="(max-width: 640px) 32px, 40px"
                              />
                            ) : (
                              <div className="w-full h-full rounded-full border bg-muted flex items-center justify-center text-sm font-semibold uppercase">
                                {airline.airline_name?.slice(0, 1) ?? "A"}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="text-xs sm:text-sm col-span-8 w-full min-w-0">
                          <div className="min-w-0">
                            <p
                              className="truncate"
                              title={airline.airline_name}
                            >
                              {airline.airline_name}
                            </p>
                            <p
                              className="truncate"
                              title={String(priceFormatter(airline.price))}
                            >
                              {priceFormatter(airline.price)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CarouselItem>
              ))}
        </CarouselContent>
        <CarouselPrevious className="left-0 md:left-2 w-7 h-7 sm:w-8 sm:h-8  text-primary z-10" />
        <CarouselNext className="right-2 w-7 h-7 sm:w-8 sm:h-8  text-primary z-10" />
      </Carousel>
    </div>
  );
};

CheapAirlineFilter.displayName = "CheapAirlineFilter";

export default CheapAirlineFilter;


